import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  MapPin, 
  Heart, 
  Share2, 
  ArrowRight, 
  CheckCircle, 
  Award, 
  DollarSign 
} from 'lucide-react';
import { Vendor } from '../../../types/vendor.ts';
import { useNavigate } from 'react-router-dom';
import { useRandomImage } from '../../../utils/imageUtils.ts';

interface StoryCardProps {
  vendor: Vendor; 
  index: number;
  featured?: boolean;
  onSelect: () => void;
  onFavoriteToggle?: () => void;
  isFavorite?: boolean;
  uniformSize?: boolean;
  compact?: boolean;
}

const StoryCard: React.FC<StoryCardProps> = ({ 
  vendor, 
  index, 
  featured = false,
  onSelect,
  onFavoriteToggle,
  isFavorite = false,
  uniformSize = false,
  compact = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(isFavorite);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  
  // Call hooks at the component level
  const image1 = useRandomImage();
  const image2 = useRandomImage();
  const image3 = useRandomImage();
  const image4 = useRandomImage();
  
  // Use vendor's images if available, otherwise use random theme images
  const vendorImages = vendor.vendorImages && vendor.vendorImages.length > 0
    ? vendor.vendorImages.map(img => img.s3Url)
    : [image1, image2, image3, image4];
  
  // Update isLiked when isFavorite prop changes
  useEffect(() => {
    setIsLiked(isFavorite);
  }, [isFavorite]);

  // Auto-advance images when hovered
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isHovered) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % vendorImages.length);
      }, 2000); // Change image every 2 seconds
    }
    return () => clearInterval(interval);
  }, [isHovered, vendorImages.length]);

  // Log vendor image information
  useEffect(() => {
    console.log(`StoryCard for ${vendor.name}:`, {
      id: vendor.id,
      name: vendor.name,
      type: vendor.vendorType,
      hasImages: vendor.vendorImages && vendor.vendorImages.length > 0,
      imageCount: vendor.vendorImages?.length || 0,
      imageUrls: vendorImages,
      currentIndex: currentImageIndex,
      isLoaded: isImageLoaded,
      hasError: imageError,
      isVisible: true,
      isHovered,
      isLiked,
      featured
    });
  }, [vendor.name, vendorImages, currentImageIndex, isImageLoaded, imageError, isHovered, isLiked, featured]);

  // Generate unique styles based on vendor data
  const getVendorStyle = () => {
    // Create unique color accent based on specialty
    const colorMap: Record<string, string> = {
      'Luxury': 'bg-rose-400',
      'Modern': 'bg-blue-400',
      'Historic': 'bg-amber-400',
      'Rustic': 'bg-emerald-400',
      'Beach': 'bg-cyan-400',
      'Garden': 'bg-lime-400',
      'Mountain': 'bg-indigo-400',
      'Urban': 'bg-violet-400',
      'Fine': 'bg-fuchsia-400'
    };
    
    // Find color match from specialty
    let accentColor = 'bg-purple-400';
    for (const [key, value] of Object.entries(colorMap)) {
      if (vendor.specialty?.includes(key)) {
        accentColor = value;
        break;
      }
    }
    
    return { accentColor };
  };
  
  const { accentColor } = getVendorStyle();

  // Get price range display based on approximateCharge
  const getPriceRange = (price: number | undefined) => {
    if (!price) return { symbol: '$', label: 'Budget Friendly' };
    if (price < 2000) return { symbol: '$', label: 'Budget Friendly' };
    if (price < 5000) return { symbol: '$$', label: 'Mid-Range' };
    return { symbol: '$$$', label: 'Luxury' };
  };

  const priceInfo = getPriceRange(vendor.approximateCharge);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
    onFavoriteToggle?.();
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest('.favorite-button')) {
      onSelect();
    }
  };

  // Log when the card is rendered
  useEffect(() => {
    console.log(`StoryCard rendered for ${vendor.name}:`, {
      id: vendor.id,
      name: vendor.name,
      type: vendor.vendorType,
      index,
      featured,
      isVisible: true,
      hasImages: vendor.vendorImages && vendor.vendorImages.length > 0,
      imageCount: vendor.vendorImages?.length || 0,
      isImageLoaded,
      hasError: imageError
    });
  }, [vendor.id, vendor.name, vendor.vendorType, index, featured, isImageLoaded, imageError]);

  // Add detailed logging for each StoryCard instance
  useEffect(() => {
    console.log(`StoryCard - Rendering Vendor:`, {
      id: vendor.id,
      name: vendor.name,
      type: vendor.vendorType,
      index,
      featured,
      hasImages: vendor.vendorImages && vendor.vendorImages.length > 0,
      imageCount: vendor.vendorImages?.length || 0,
      imageUrls: vendor.vendorImages?.map(img => img.s3Url) || [],
      isVisible: true,
      isHovered,
      isLiked
    });
  }, [vendor.id, vendor.name, vendor.vendorType, index, featured, isHovered, isLiked]);

  // Add logging for image loading
  useEffect(() => {
    console.log(`StoryCard - Image Loading Status for ${vendor.name}:`, {
      currentImageIndex,
      totalImages: vendorImages.length,
      isImageLoaded,
      hasError: imageError,
      currentImageUrl: vendorImages[currentImageIndex]
    });
  }, [vendor.name, currentImageIndex, isImageLoaded, imageError, vendorImages]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ 
        opacity: 1, 
        y: 0,
        scale: 1,
        transition: { 
          type: "spring", 
          stiffness: 80, 
          damping: 15,
          delay: featured ? 0 : index * 0.1
        }
      }}
      viewport={{ once: true, margin: "-100px" }}
      whileHover={{ 
        y: -5,
        transition: { duration: 0.2 } 
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleCardClick}
      className={`group relative cursor-pointer ${
        compact
          ? 'w-full aspect-[2/1]'
          : uniformSize
          ? 'w-full aspect-[4/5]'
          : featured 
          ? 'w-full aspect-[16/9]' 
          : 'w-full aspect-[3/4]'
      }`}
      style={{
        filter: isHovered ? 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))' : 'none',
        transition: 'filter 0.3s ease'
      }}
    >
      {/* Main card container with glassmorphism */}
      <div 
        className={`absolute inset-0 rounded-xl overflow-hidden ${
          isHovered ? 'shadow-2xl shadow-white/20' : 'shadow-xl'
        } backdrop-blur-[12px] bg-white/15 border border-white/20 transition-all duration-300`}
      >
        {/* Image carousel with enhanced gradient overlay for readability */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/10 z-10" />
          <AnimatePresence mode="sync">
            <motion.img 
              key={currentImageIndex}
              src={vendorImages[currentImageIndex]}
              alt={`${vendor.name} - ${vendor.specialty || 'Wedding Vendor'}`}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              onLoad={() => {
                console.log(`Image loaded for ${vendor.name}:`, {
                  imageUrl: vendorImages[currentImageIndex],
                  imageIndex: currentImageIndex,
                  isLoaded: true
                });
                setIsImageLoaded(true);
                setImageError(false);
              }}
              onError={(e) => {
                console.error(`Error loading image for ${vendor.name}:`, {
                  imageUrl: vendorImages[currentImageIndex],
                  error: e,
                  imageIndex: currentImageIndex,
                  isLoaded: false
                });
                setImageError(true);
                setIsImageLoaded(false);
              }}
            />
          </AnimatePresence>
          
          {/* Image navigation dots */}
          <div className={`absolute ${compact ? 'bottom-2' : 'bottom-4'} left-1/2 transform -translate-x-1/2 z-20 flex ${compact ? 'gap-1' : 'gap-1.5'}`}>
            {vendorImages.map((_, idx) => (
              <motion.div
                key={idx}
                className={`${compact ? 'w-1 h-1' : 'w-1.5 h-1.5'} rounded-full ${
                  idx === currentImageIndex ? 'bg-white' : 'bg-white/40'
                }`}
                animate={{
                  scale: idx === currentImageIndex ? 1.2 : 1,
                  opacity: idx === currentImageIndex ? 1 : 0.6
                }}
                transition={{ duration: 0.2 }}
              />
            ))}
          </div>
        </div>
        
        {/* Status badges */}
        <div className={`absolute ${compact ? 'top-1 left-1' : 'top-4 left-4'} z-20 flex items-center gap-1`}>
          {vendor.verified && (
            <motion.div 
              className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} rounded-full bg-[#FF4E8E] flex items-center justify-center text-white shadow-lg`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <CheckCircle size={compact ? 10 : 16} />
            </motion.div>
          )}
          {vendor.topVendor && (
            <motion.div 
              className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} rounded-full bg-[#8A2BE2] flex items-center justify-center text-white shadow-lg`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Award size={compact ? 10 : 16} />
            </motion.div>
          )}
        </div>
        
        {/* Price indicator */}
        <div className={`absolute ${compact ? 'top-1 right-1' : 'top-4 right-4'} z-20`}>
          <motion.div 
            className={`${compact ? 'px-1.5 py-0.5' : 'px-3 py-1'} rounded-full bg-[#FFB02E] flex items-center justify-center text-white ${compact ? 'text-xs' : 'text-sm'} font-medium shadow-lg`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="tracking-wider">{priceInfo.symbol}</span>
          </motion.div>
        </div>
        
        {/* Card content */}
        <div className={`absolute bottom-0 left-0 right-0 ${compact ? 'p-2' : 'p-5'} z-20`}>
          <h3 className={`text-white ${compact ? 'text-xs' : 'text-xl'} font-medium ${compact ? 'mb-0.5' : 'mb-2'} tracking-wide truncate`}>
            {vendor.name}
          </h3>
          
          <div className="flex justify-between items-end">
            <div className={`space-y-0.5 ${compact ? 'mb-0.5' : 'mb-2'}`}>
              <div className="flex items-center gap-1 text-white/90">
                <MapPin className={`${compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'}`} />
                <span className={`${compact ? 'text-xs' : 'text-sm'} truncate max-w-[80px]`}>{vendor.location}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <div className="flex">
                  {[...Array(5)].map((_, i) => {
                    const starValue = i + 1;
                    const rating = vendor.googlePlaceData?.rating || vendor.rating || 5;
                    return (
                      <Star 
                        key={i} 
                        className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} ${starValue <= Math.round(rating) ? 'fill-amber-300 text-amber-300' : 'text-white/40'}`} 
                      />
                    );
                  })}
                </div>
                <span className={`text-white ${compact ? 'text-xs' : 'text-xs'} ml-0.5`}>
                  {vendor.googlePlaceData ? (
                    compact ? (
                      vendor.googlePlaceData.rating.toFixed(1)
                    ) : (
                      <>
                        {vendor.googlePlaceData.rating.toFixed(1)}
                        <span className="text-white/70 ml-1">
                          ({vendor.googlePlaceData.user_ratings_total})
                        </span>
                      </>
                    )
                  ) : (
                    vendor.rating?.toFixed(1) || "5.0"
                  )}
                </span>
              </div>
            </div>
            
            {!compact && (
              <div className="flex gap-2">
                <motion.button 
                  onClick={handleFavoriteClick}
                  className={`favorite-button w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isLiked 
                      ? 'bg-[#FF4E8E] text-white' 
                      : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={isLiked ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart className={`w-5 h-5 ${isLiked ? 'fill-white' : ''}`} />
                </motion.button>
                
                <motion.button 
                  className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/20 transition-all"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const vendorUrl = `${window.location.origin}/connect/vendor/${vendor.id}/${vendor.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')}`;
                    if (navigator.share) {
                      navigator.share({
                        title: vendor.name,
                        text: `Check out ${vendor.name} on Wedify!`,
                        url: vendorUrl
                      }).catch(console.error);
                    } else {
                      // Fallback for browsers that don't support Web Share API
                      navigator.clipboard.writeText(vendorUrl);
                      // You might want to add a toast notification here
                      alert('Link copied to clipboard!');
                    }
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Share vendor"
                >
                  <Share2 className="w-5 h-5" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* View Details button - only in the middle */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
          >
            <motion.div 
              className={`bg-white/20 backdrop-blur-md border border-white/20 rounded-full ${compact ? 'px-3 py-1' : 'px-4 py-1.5'} shadow-lg pointer-events-auto`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect();
              }}
            >
              <span className={`text-white ${compact ? 'text-xs' : 'text-sm'} font-medium flex items-center`}>
                {compact ? 'View' : 'View Details'} <ArrowRight className={`${compact ? 'w-3 h-3 ml-1' : 'w-3.5 h-3.5 ml-1.5'}`} />
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StoryCard; 